import { create } from 'zustand'
import * as authApi from '@/api/auth'
import {
  getStoredSessionToken,
  getStoredRenewToken,
  setStoredSessionToken,
  setStoredRenewToken,
  clearStoredTokens,
} from '@/api/client'
import type { FullUserResponseDto } from '@/types/api'

const POLL_INTERVAL_START_MS = 2000
const POLL_INTERVAL_MAX_MS = 10_000
const POLL_TIMEOUT_MS = 5 * 60 * 1000

interface AuthState {
  sessionToken: string | null
  renewToken: string | null
  userDetails: FullUserResponseDto | null
  isAuthenticated: () => boolean
  setTokens: (sessionToken: string | null, renewToken: string | null) => void
  loginWithEmail: (email: string) => Promise<string>
  pollUntilComplete: (confirmToken: string, signal?: AbortSignal) => Promise<void>
  completeWithEmailToken: (emailToken: string) => Promise<void>
  fetchUserDetails: () => Promise<void>
  logout: () => void
}

export const useAuthStore = create<AuthState>((set, get) => ({
  sessionToken: getStoredSessionToken(),
  renewToken: getStoredRenewToken(),
  userDetails: null,

  isAuthenticated: () => Boolean(get().sessionToken),

  setTokens: (sessionToken, renewToken) => {
    setStoredSessionToken(sessionToken)
    setStoredRenewToken(renewToken)
    set({ sessionToken, renewToken })
  },

  async loginWithEmail(email) {
    const { confirmToken } = await authApi.validateEmailForLogin(email)
    return confirmToken
  },

  async pollUntilComplete(confirmToken, signal) {
    const started = Date.now()
    let interval = POLL_INTERVAL_START_MS
    while (Date.now() - started < POLL_TIMEOUT_MS) {
      if (signal?.aborted) throw new Error('Cancelled')
      const data = await authApi.pollEmailValidation(confirmToken)
      const access = authApi.pickAccessToken(data)
      if (data.completed && access && data.renewToken) {
        get().setTokens(access, data.renewToken)
        await get().fetchUserDetails()
        return
      }
      await new Promise((r) => setTimeout(r, interval))
      interval = Math.min(interval * 1.5, POLL_INTERVAL_MAX_MS)
    }
    throw new Error('Login timed out')
  },

  async completeWithEmailToken(emailToken) {
    const data = await authApi.completeEmailValidation(emailToken)
    const access = authApi.pickAccessToken(data)
    if (!access || !data.renewToken) throw new Error('Invalid login link')
    get().setTokens(access, data.renewToken)
    await get().fetchUserDetails()
  },

  async fetchUserDetails() {
    const details = await authApi.getUserDetails()
    set({ userDetails: details })
  },

  logout() {
    clearStoredTokens()
    set({ sessionToken: null, renewToken: null, userDetails: null })
  },
}))
